import { Button, IconButton } from '@ads/components-react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import * as Dialog from '@radix-ui/react-dialog';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/shared/ui/atoms';
import { AlternativeList, QuestionSection } from '@/shared/ui/molecules';

import { useQuestionPreview } from '../../../hooks/useQuestionPreview';

// ─── Tipos ────────────────────────────────────────────────────────────────────

/** Props do QuestionPreviewModal */
export interface QuestionPreviewModalProps {
  /** ID da questão a visualizar, ou null quando o modal deve estar fechado */
  readonly questionId: string | null;
  /** Callback ao fechar o modal */
  readonly onClose: () => void;
}

// ─── Skeleton ─────────────────────────────────────────────────────────────────

function PreviewSkeleton() {
  return (
    <div className="flex flex-col gap-sm p-lg">
      <Skeleton className="h-mini w-full" />
      <Skeleton className="h-mini w-5/6" />
      <Skeleton className="mt-xs h-mini w-3/4" />
      <div className="mt-sm flex flex-col gap-nano">
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="flex items-center gap-nano">
            <Skeleton className="size-xs shrink-0 rounded-circular" />
            <Skeleton className="h-micro flex-1" />
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Componente ───────────────────────────────────────────────────────────────

/**
 * Modal de visualização de uma questão da Biblioteca de Questões.
 *
 * Exibe o enunciado (HTML) e as alternativas com indicação de qual é a correta.
 * Usa `useQuestionPreview` para buscar os dados.
 *
 * @example
 * ```tsx
 * <QuestionPreviewModal questionId={selectedId} onClose={() => setSelectedId(null)} />
 * ```
 */
export function QuestionPreviewModal({ questionId, onClose }: QuestionPreviewModalProps) {
  const { t } = useTranslation('questionLibrary');
  const { preview, isLoading } = useQuestionPreview(questionId);

  const isOpen = questionId !== null;

  return (
    <Dialog.Root open={isOpen} onOpenChange={open => !open && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-none z-50 h-screen w-screen bg-base-gray-900 bg-opacity-medium backdrop-blur-soft" />

        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 flex max-h-[85vh] w-full max-w-3xl -translate-x-1/2 -translate-y-1/2 flex-col rounded-md bg-ctx-layout-surface shadow-level3 focus:outline-none"
          aria-describedby={undefined}
        >
          {/* Header */}
          <div className="flex items-center justify-between border-b-hairline border-ctx-layout-border px-lg py-xxs">
            <Dialog.Title className="text-ctx-content-title ts-heading-xs">
              {t('previewModal.title')}
            </Dialog.Title>
            <Dialog.Close asChild>
              <IconButton
                icon={XMarkIcon}
                hierarchy="tertiary"
                size="sm"
                ariaLabel={t('previewModal.close')}
              />
            </Dialog.Close>
          </div>

          {/* Body */}
          <div className="at-scrollbar flex-1 overflow-y-auto">
            {isLoading ? (
              <PreviewSkeleton />
            ) : !preview ? null : (
              <div className="flex flex-col gap-xxs px-md py-mini">
                {/* Enunciado */}
                <QuestionSection label={t('previewModal.statement')}>
                  <p
                    className="rich-text-content ts-paragraph-xs text-ctx-content-base [&_.code-display]:rounded-sm [&_.code-display]:bg-gray-100 [&_.code-display]:px-2 [&_.code-display]:py-1 [&_.code-display]:font-mono [&_.code-display]:text-sm [&_.code-display]:text-gray-800 [&_*]:!font-base [&_*]:!text-xs [&_*]:!leading-lg [&_*]:!text-ctx-content-base"
                    dangerouslySetInnerHTML={{ __html: preview.text }}
                  />
                </QuestionSection>

                {/* Alternativas */}
                <div className="flex flex-col gap-nano">
                  <span className="ts-heading-xxs text-ctx-content-title">
                    {t('previewModal.alternatives')}
                  </span>
                  {preview.alternatives.length > 0 ? (
                    <AlternativeList
                      options={preview.alternatives.map(a => ({
                        id: Number(a.id),
                        value: a.value,
                        checked: a.isCorrect,
                      }))}
                    />
                  ) : (
                    <p className="italic ts-caption-xxs text-ctx-content-placeholder">
                      {t('previewModal.openQuestion')}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end border-t-hairline border-ctx-layout-border px-lg py-xxs">
            <Dialog.Close asChild>
              <Button hierarchy="secondary" size="md">
                {t('previewModal.close')}
              </Button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
